/**
 * Band designs W4 (contract v67) — the inside-the-band LOGO, host side.
 *
 * The viewer accepts `EngravingLogo.svg` only as NORMALISED PATH DATA (the
 * commands M L H V C S Q T A Z, numbers, spaces and commas, starting with a
 * moveto, ≤ `ENGRAVING_LOGO_SVG_MAX_CHARS`) and rejects the whole logo on
 * anything else. A jeweller uploads an .svg FILE, so this module flattens one:
 *
 *   · it never parses the file as a document (no DOMParser, no <img>): a small
 *     tag scanner reads the drawing elements and IGNORES everything else —
 *     scripts, styles, <use>/hrefs, foreignObject, defs/clipPath/mask/symbol
 *     content — so nothing in the file can run or fetch;
 *   · path / rect / circle / ellipse / polygon / polyline become path data,
 *     every `transform` (own and ancestor <g>) is baked in, arcs become
 *     cubics, and the result is re-emitted as absolute M / L / C / Q / Z,
 *     scaled to a 100-unit-tall box and rounded — the same canonical grammar
 *     the viewer's `parseLogoPath` emits, and a subset of its whitelist;
 *   · a stroke-only shape has no fill area and is skipped (said in a note).
 *
 * Raster logos (PNG / JPEG / WebP / GIF) go to the Vault as an asset when the
 * user is signed in (a short URL that can travel in a share link); otherwise
 * they stay a `data:` URL on this device only — `designStore.saveDesign`
 * strips one from the share token and the Engrave tab says so.
 *
 * Pure except `readFileAsDataUrl`, so `scripts/check-ring-body.ts` runs it
 * headless and feeds every output to the VIEWER's own parser.
 */
import { ENGRAVING_LOGO_SVG_MAX_CHARS } from "../../../shared/api-contract";
import type { EngravingLogo, EngravingLogoMode } from "../../../shared/api-contract";

/** The viewer's whitelist, verbatim (`band/engravingLogo.ts` PATH_CHARSET). */
export const LOGO_PATH_CHARSET = /^[MmLlHhVvCcSsQqTtAaZz0-9eE+\-.,\s]*$/;

/** Does this string pass the viewer's first gate (charset, moveto first, length)? */
export function isLogoPathData(d: unknown): d is string {
  return typeof d === "string" && d.length > 0 && d.length <= ENGRAVING_LOGO_SVG_MAX_CHARS
    && LOGO_PATH_CHARSET.test(d) && /^\s*[Mm]/.test(d);
}

/** The logo height the viewer clamps to (`LOGO_HEIGHT_MIN_MM` … `_MAX_MM`). */
export const LOGO_HEIGHT = { min: 0.3, max: 6, step: 0.1, start: 1.5 } as const;

/** A raster larger than this is refused: it would ride every undo snapshot. */
export const LOGO_RASTER_MAX_BYTES = 1_500_000;

type Mat = [number, number, number, number, number, number]; // a b c d e f
const ID: Mat = [1, 0, 0, 1, 0, 0];
const mul = (m: Mat, n: Mat): Mat => [
  m[0] * n[0] + m[2] * n[1], m[1] * n[0] + m[3] * n[1],
  m[0] * n[2] + m[2] * n[3], m[1] * n[2] + m[3] * n[3],
  m[0] * n[4] + m[2] * n[5] + m[4], m[1] * n[4] + m[3] * n[5] + m[5],
];
const apply = (m: Mat, x: number, y: number): [number, number] => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];

const numList = (s: string) => (s.match(/[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g) ?? []).map(Number);

/** SVG `transform` attribute → matrix. Unknown functions make the element unusable (null). */
export function parseTransform(t: string | undefined): Mat | null {
  if (!t || !t.trim()) return ID;
  let m: Mat = ID;
  const re = /([a-zA-Z]+)\s*\(([^)]*)\)/g;
  let found: RegExpExecArray | null;
  let consumed = "";
  while ((found = re.exec(t))) {
    consumed += found[0];
    const a = numList(found[2]);
    let n: Mat;
    switch (found[1]) {
      case "matrix": if (a.length !== 6) return null; n = a as Mat; break;
      case "translate": n = [1, 0, 0, 1, a[0] ?? 0, a[1] ?? 0]; break;
      case "scale": n = [a[0] ?? 1, 0, 0, a[1] ?? a[0] ?? 1, 0, 0]; break;
      case "rotate": {
        const r = ((a[0] ?? 0) * Math.PI) / 180;
        const c = Math.cos(r), s = Math.sin(r);
        const rot: Mat = [c, s, -s, c, 0, 0];
        if (a.length >= 3) n = mul(mul([1, 0, 0, 1, a[1], a[2]], rot), [1, 0, 0, 1, -a[1], -a[2]]);
        else n = rot;
        break;
      }
      case "skewX": n = [1, 0, Math.tan(((a[0] ?? 0) * Math.PI) / 180), 1, 0, 0]; break;
      case "skewY": n = [1, Math.tan(((a[0] ?? 0) * Math.PI) / 180), 0, 1, 0, 0]; break;
      default: return null;
    }
    if (!n.every(Number.isFinite)) return null;
    m = mul(m, n);
  }
  return consumed.replace(/\s|,/g, "").length === t.replace(/\s|,/g, "").length ? m : null;
}

/** Arc (endpoint form) → cubics, ≤ 90° each (W3C SVG 1.1 § F.6.5), as the viewer does it. */
function arcToCubics(x1: number, y1: number, rxIn: number, ryIn: number, phiDeg: number, large: boolean, sweep: boolean, x2: number, y2: number): number[][] {
  let rx = Math.abs(rxIn), ry = Math.abs(ryIn);
  if (x1 === x2 && y1 === y2) return [];
  if (rx === 0 || ry === 0) return [[x1, y1, x2, y2, x2, y2]];
  const phi = (phiDeg * Math.PI) / 180, cos = Math.cos(phi), sin = Math.sin(phi);
  const dx = (x1 - x2) / 2, dy = (y1 - y2) / 2;
  const x1p = cos * dx + sin * dy, y1p = -sin * dx + cos * dy;
  const lambda = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
  if (lambda > 1) { const s = Math.sqrt(lambda); rx *= s; ry *= s; }
  const num = rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p;
  const den = rx * rx * y1p * y1p + ry * ry * x1p * x1p;
  let co = Math.sqrt(Math.max(0, num / den));
  if (large === sweep) co = -co;
  const cxp = (co * rx * y1p) / ry, cyp = (-co * ry * x1p) / rx;
  const cx = cos * cxp - sin * cyp + (x1 + x2) / 2, cy = sin * cxp + cos * cyp + (y1 + y2) / 2;
  const ang = (ux: number, uy: number, vx: number, vy: number) => Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy);
  const t1 = ang(1, 0, (x1p - cxp) / rx, (y1p - cyp) / ry);
  let dt = ang((x1p - cxp) / rx, (y1p - cyp) / ry, (-x1p - cxp) / rx, (-y1p - cyp) / ry);
  if (!sweep && dt > 0) dt -= 2 * Math.PI; else if (sweep && dt < 0) dt += 2 * Math.PI;
  const segs = Math.max(1, Math.ceil(Math.abs(dt) / (Math.PI / 2) - 1e-9));
  const step = dt / segs, k = (4 / 3) * Math.tan(step / 4);
  const pt = (t: number) => { const ex = rx * Math.cos(t), ey = ry * Math.sin(t); return [cos * ex - sin * ey + cx, sin * ex + cos * ey + cy]; };
  const der = (t: number) => { const ex = -rx * Math.sin(t), ey = ry * Math.cos(t); return [cos * ex - sin * ey, sin * ex + cos * ey]; };
  const out: number[][] = [];
  for (let i = 0; i < segs; i++) {
    const a = t1 + i * step, b = a + step;
    const [ax, ay] = pt(a);
    const [bx, by] = i === segs - 1 ? [x2, y2] : pt(b);
    const [dax, day] = der(a), [dbx, dby] = der(b);
    out.push([ax + k * dax, ay + k * day, bx - k * dbx, by - k * dby, bx, by]);
  }
  return out;
}

/** One absolute segment: M/L (2 numbers), C (6), Q (4), Z (0). */
interface Seg { c: "M" | "L" | "C" | "Q" | "Z"; p: number[] }

/** Path data → absolute M/L/C/Q/Z segments, or null when it is not path data. */
export function pathSegments(d: string): Seg[] | null {
  if (!LOGO_PATH_CHARSET.test(d)) return null;
  const out: Seg[] = [];
  let i = 0, cmd = "", cx = 0, cy = 0, sx = 0, sy = 0;
  let lastC: number[] | null = null, lastQ: number[] | null = null;
  const n = d.length;
  const skip = () => { while (i < n && /[\s,]/.test(d[i])) i++; };
  const NUM = /[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/y;
  const num = () => { skip(); NUM.lastIndex = i; const m = NUM.exec(d); if (!m) return null; i = NUM.lastIndex; const v = Number(m[0]); return Number.isFinite(v) ? v : null; };
  // Arc flags may be packed ("a1 1 0 011 1"): one character each, as the viewer reads them.
  const flag = () => { skip(); if (d[i] === "0" || d[i] === "1") return d[i++] === "1"; return null; };
  skip();
  if (i < n && !/[Mm]/.test(d[i])) return null;
  while (true) {
    skip();
    if (i >= n) break;
    if (/[A-Za-z]/.test(d[i])) cmd = d[i++];
    else if (!cmd || /[zZ]/.test(cmd)) return null;
    const rel = cmd === cmd.toLowerCase();
    const C = cmd.toUpperCase();
    const ox = rel ? cx : 0, oy = rel ? cy : 0;
    if (C === "Z") { out.push({ c: "Z", p: [] }); cx = sx; cy = sy; lastC = lastQ = null; continue; }
    if (C === "M") {
      const x = num(), y = num(); if (x === null || y === null) return null;
      cx = sx = ox + x; cy = sy = oy + y; out.push({ c: "M", p: [cx, cy] });
      lastC = lastQ = null; cmd = rel ? "l" : "L"; continue;
    }
    if (C === "L" || C === "H" || C === "V") {
      let x = cx, y = cy;
      if (C === "L") { const a = num(), b = num(); if (a === null || b === null) return null; x = ox + a; y = oy + b; }
      else { const a = num(); if (a === null) return null; if (C === "H") x = (rel ? cx : 0) + a; else y = (rel ? cy : 0) + a; }
      out.push({ c: "L", p: [x, y] }); cx = x; cy = y; lastC = lastQ = null;
    } else if (C === "C" || C === "S") {
      let x1: number, y1: number;
      if (C === "C") { const a = num(), b = num(); if (a === null || b === null) return null; x1 = ox + a; y1 = oy + b; }
      else { x1 = lastC ? 2 * cx - lastC[0] : cx; y1 = lastC ? 2 * cy - lastC[1] : cy; }
      const c2 = num(), e = num(), f = num(), g = num(); if (c2 === null || e === null || f === null || g === null) return null;
      out.push({ c: "C", p: [x1, y1, ox + c2, oy + e, ox + f, oy + g] });
      lastC = [ox + c2, oy + e]; lastQ = null; cx = ox + f; cy = oy + g;
    } else if (C === "Q" || C === "T") {
      let x1: number, y1: number;
      if (C === "Q") { const a = num(), b = num(); if (a === null || b === null) return null; x1 = ox + a; y1 = oy + b; }
      else { x1 = lastQ ? 2 * cx - lastQ[0] : cx; y1 = lastQ ? 2 * cy - lastQ[1] : cy; }
      const f = num(), g = num(); if (f === null || g === null) return null;
      out.push({ c: "Q", p: [x1, y1, ox + f, oy + g] });
      lastQ = [x1, y1]; lastC = null; cx = ox + f; cy = oy + g;
    } else if (C === "A") {
      const rx = num(), ry = num(), rot = num(), large = flag(), sweep = flag(), f = num(), g = num();
      if (rx === null || ry === null || rot === null || large === null || sweep === null || f === null || g === null) return null;
      for (const s of arcToCubics(cx, cy, rx, ry, rot, large, sweep, ox + f, oy + g)) out.push({ c: "C", p: s });
      cx = ox + f; cy = oy + g; lastC = lastQ = null;
    } else return null;
  }
  return out;
}

/** Elements whose CONTENT is never drawn (definitions, metadata, active content). */
const SKIP_CONTENT = new Set(["defs", "clippath", "mask", "symbol", "pattern", "marker", "style", "script", "title", "desc", "metadata", "foreignobject", "lineargradient", "radialgradient", "filter", "switch"]);

function attrs(s: string): Record<string, string> {
  const out: Record<string, string> = {};
  const re = /([\w:.-]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s))) out[m[1].toLowerCase()] = m[2] ?? m[3] ?? "";
  // `style="fill:none;fill-rule:evenodd"` wins over the presentation attribute, as in CSS.
  for (const decl of (out.style ?? "").split(";")) {
    const [k, v] = decl.split(":").map((x) => x?.trim().toLowerCase());
    if ((k === "fill" || k === "fill-rule" || k === "display" || k === "visibility") && v) out[k] = v;
  }
  return out;
}

const f = (n: string | undefined, d = 0) => { const v = Number(n); return Number.isFinite(v) ? v : d; };

function elementPath(tag: string, a: Record<string, string>): string | null {
  switch (tag) {
    case "path": return a.d ?? null;
    case "rect": {
      const x = f(a.x), y = f(a.y), w = f(a.width), h = f(a.height);
      if (!(w > 0 && h > 0)) return null;
      let rx = a.rx !== undefined ? f(a.rx) : a.ry !== undefined ? f(a.ry) : 0;
      let ry = a.ry !== undefined ? f(a.ry) : rx;
      rx = Math.min(Math.max(0, rx), w / 2); ry = Math.min(Math.max(0, ry), h / 2);
      if (!rx || !ry) return `M${x} ${y}L${x + w} ${y}L${x + w} ${y + h}L${x} ${y + h}Z`;
      return `M${x + rx} ${y}L${x + w - rx} ${y}A${rx} ${ry} 0 0 1 ${x + w} ${y + ry}L${x + w} ${y + h - ry}A${rx} ${ry} 0 0 1 ${x + w - rx} ${y + h}`
        + `L${x + rx} ${y + h}A${rx} ${ry} 0 0 1 ${x} ${y + h - ry}L${x} ${y + ry}A${rx} ${ry} 0 0 1 ${x + rx} ${y}Z`;
    }
    case "circle": case "ellipse": {
      const cx = f(a.cx), cy = f(a.cy);
      const rx = tag === "circle" ? f(a.r) : f(a.rx), ry = tag === "circle" ? f(a.r) : f(a.ry);
      if (!(rx > 0 && ry > 0)) return null;
      return `M${cx - rx} ${cy}A${rx} ${ry} 0 1 0 ${cx + rx} ${cy}A${rx} ${ry} 0 1 0 ${cx - rx} ${cy}Z`;
    }
    case "polygon": case "polyline": {
      const p = numList(a.points ?? "");
      if (p.length < 6) return null;
      let d = `M${p[0]} ${p[1]}`;
      for (let i = 2; i + 1 < p.length; i += 2) d += `L${p[i]} ${p[i + 1]}`;
      return `${d}Z`;
    }
    default: return null;
  }
}

export type SvgLogoResult =
  | { ok: true; svg: string; fillRule: "nonzero" | "evenodd"; shapes: number; notes: string[] }
  | { ok: false; reason: string };

/**
 * An uploaded .svg file's TEXT → the logo's normalised path data.
 *
 * The output is absolute M/L/C/Q/Z, the drawing scaled so its ink is 100
 * units tall (the viewer sizes it by `heightMM` from its own bounding box, so
 * the scale is free) and rounded to 0.1 unit (0.1% of the height). Too
 * detailed to fit ENGRAVING_LOGO_SVG_MAX_CHARS even at whole units ⇒ refused.
 */
/** Widest (or tallest) a logo may be against its other side (`svgFileToLogoPath`). */
export const LOGO_MAX_ASPECT = 1000;

/**
 * The GRAMMAR the viewer's `parseLogoPath` reads, for the output this module
 * writes: absolute M / L / C / Q / Z, each with its whole set of finite
 * numbers within ±1e6. The charset test alone passed `M0 0L1e9 0…`, which the
 * viewer refused ("malformed L") while the panel showed nothing.
 */
export function isCanonicalPath(d: string): boolean {
  const ARITY: Record<string, number> = { M: 2, L: 2, C: 6, Q: 4, Z: 0 };
  const toks = d.match(/[MLCQZ]|-?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?/gi);
  if (!toks || toks[0] !== "M") return false;
  let i = 0;
  while (i < toks.length) {
    const n = ARITY[toks[i]];
    if (n === undefined) return false;
    i++;
    let got = 0;
    while (i < toks.length && ARITY[toks[i]] === undefined) {
      const v = Number(toks[i]);
      if (!Number.isFinite(v) || Math.abs(v) > 1e6) return false;
      got++; i++;
    }
    if (n === 0 ? got !== 0 : got === 0 || got % n !== 0) return false;
  }
  return true;
}

export function svgFileToLogoPath(text: string): SvgLogoResult {
  if (typeof text !== "string" || !text.trim()) return { ok: false, reason: "The file is empty." };
  if (text.length > 4_000_000) return { ok: false, reason: "The file is larger than 4 MB." };
  const src = text.replace(/<!--[\s\S]*?-->/g, "").replace(/<!\[CDATA\[[\s\S]*?\]\]>/g, "").replace(/<\?[\s\S]*?\?>/g, "").replace(/<!DOCTYPE[\s\S]*?>/gi, "");
  if (!/<svg[\s>]/i.test(src)) return { ok: false, reason: "This is not an SVG file." };

  const stack: { tag: string; m: Mat | null; skip: boolean; hidden: boolean; noFill: boolean }[] = [];
  const segs: Seg[] = [];
  let shapes = 0, strokeOnly = 0, evenodd = false, nonzero = false, unusable = 0;
  // Simple <style> rules that turn the fill off (`.a{fill:none}`, `path{fill:none}`):
  // a class-styled outline is a stroke, not ink (it used to be engraved filled).
  const noFillClass = new Set<string>(), noFillTag = new Set<string>();
  for (const block of src.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi)) {
    for (const rule of block[1].matchAll(/([^{}]+)\{([^}]*)\}/g)) {
      if (!/(^|;)\s*fill\s*:\s*(none|transparent)\b/i.test(rule[2])) continue;
      for (const sel of rule[1].split(",").map((x) => x.trim())) {
        if (/^\.[\w-]+$/.test(sel)) noFillClass.add(sel.slice(1));
        else if (/^[a-z]+$/i.test(sel)) noFillTag.add(sel.toLowerCase());
      }
    }
  }
  // Attribute values may contain ">" (data-x="a>b"): quoted values are skipped whole.
  const tagRe = /<(\/?)([a-zA-Z][\w:.-]*)((?:\s+(?:[^>"']|"[^"]*"|'[^']*')*?)?)(\/?)>/g;
  let t: RegExpExecArray | null;
  while ((t = tagRe.exec(src))) {
    const closing = t[1] === "/", tag = t[2].toLowerCase().replace(/^svg:/, ""), selfClose = t[4] === "/" || /\/\s*$/.test(t[3]);
    if (closing) {
      // Pop to the matching open tag (tolerates sloppy nesting).
      for (let k = stack.length - 1; k >= 0; k--) if (stack[k].tag === tag) { stack.length = k; break; }
      continue;
    }
    const a = attrs(t[3]);
    const parent = stack[stack.length - 1];
    const own = parseTransform(a.transform);
    const m = parent?.m === null || own === null ? null : mul(parent?.m ?? ID, own);
    const skip = (parent?.skip ?? false) || SKIP_CONTENT.has(tag);
    const hidden = (parent?.hidden ?? false) || a.display === "none" || a.visibility === "hidden";
    const classNoFill = (a.class ?? "").split(/\s+/).some((c) => noFillClass.has(c)) || noFillTag.has(tag);
    const noFill = a.fill !== undefined ? a.fill === "none" || a.fill === "transparent" : classNoFill || (parent?.noFill ?? false);
    if (!selfClose) stack.push({ tag, m, skip, hidden, noFill });
    if (skip || hidden) continue;
    const d = elementPath(tag, a);
    if (d === null) continue;
    if (noFill) { strokeOnly++; continue; }
    if (m === null) { unusable++; continue; }
    const ps = pathSegments(d);
    if (!ps) { unusable++; continue; }
    if ((a["fill-rule"] ?? "") === "evenodd") evenodd = true;
    else nonzero = true;
    shapes++;
    for (const s of ps) {
      const p: number[] = [];
      for (let i = 0; i < s.p.length; i += 2) p.push(...apply(m, s.p[i], s.p[i + 1]));
      segs.push({ c: s.c, p });
    }
  }
  if (!shapes) {
    return { ok: false, reason: strokeOnly
      ? "The drawing is made of outlines (strokes) only. Convert the strokes to filled shapes in your design tool, then upload it again."
      : "No filled shape was found in this SVG." };
  }
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const s of segs) for (let i = 0; i < s.p.length; i += 2) {
    minX = Math.min(minX, s.p[i]); maxX = Math.max(maxX, s.p[i]);
    minY = Math.min(minY, s.p[i + 1]); maxY = Math.max(maxY, s.p[i + 1]);
  }
  const h = maxY - minY, w = maxX - minX;
  if (!(h > 0) || !(w > 0) || !Number.isFinite(h + w)) return { ok: false, reason: "The drawing encloses no area." };
  // The viewer refuses coordinates past 1e6 (`parseLogoPath`); at a 100-unit
  // height that is an aspect of 10 000. A mark 1000× wider than tall is a
  // line, not a logo — refused here with a reason, instead of a path the
  // viewer drops silently.
  if (w / h > LOGO_MAX_ASPECT || h / w > LOGO_MAX_ASPECT) return { ok: false, reason: "The drawing is a thin line (over 1000 times longer than it is wide), not a logo." };
  const k = 100 / h;
  const notes: string[] = [];
  if (evenodd && nonzero) notes.push("Some shapes use the even-odd fill rule and some do not; the whole logo is engraved with even-odd.");
  if (strokeOnly) notes.push(`${strokeOnly} outline-only shape${strokeOnly === 1 ? " was" : "s were"} left out (strokes have no fill to engrave).`);
  if (unusable) notes.push(`${unusable} shape${unusable === 1 ? " was" : "s were"} left out (unreadable path or transform).`);
  for (const decimals of [1, 0]) {
    const q = 10 ** decimals;
    const fmt = (v: number) => { const r = Math.round(v * q) / q; return Object.is(r, -0) ? "0" : String(r); };
    const out: string[] = [];
    for (const s of segs) {
      const p: string[] = [];
      for (let i = 0; i < s.p.length; i += 2) p.push(`${fmt((s.p[i] - minX) * k)} ${fmt((s.p[i + 1] - minY) * k)}`);
      out.push(s.c + p.join(" "));
    }
    const d = out.join("");
    if (d.length <= ENGRAVING_LOGO_SVG_MAX_CHARS && isLogoPathData(d) && isCanonicalPath(d)) {
      if (!decimals) notes.push("The logo was simplified to fit (rounded to whole units of a 100-unit height).");
      return { ok: true, svg: d, fillRule: evenodd ? "evenodd" : "nonzero", shapes, notes };
    }
  }
  return { ok: false, reason: `The logo is too detailed: its outline does not fit in ${ENGRAVING_LOGO_SVG_MAX_CHARS} characters. Simplify it in your design tool.` };
}

/** Raster types the viewer decodes (it sniffs the bytes again itself). */
export function rasterKind(bytes: Uint8Array): "png" | "jpeg" | "webp" | "gif" | null {
  const b = bytes;
  if (b.length >= 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return "png";
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "jpeg";
  if (b.length >= 12 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50) return "webp";
  if (b.length >= 6 && b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46) return "gif";
  return null;
}

/** A logo that lives on THIS device only — a `data:` URL raster (never put in a share token). */
export const isLocalOnlyLogo = (l: EngravingLogo | null | undefined) => !!l && !l.svg && typeof l.url === "string" && /^data:/i.test(l.url);

/** The logo block a fresh upload starts from (keeps the height and mode already chosen). */
export function logoFrom(prev: EngravingLogo | null | undefined, src: { svg: string; fillRule: "nonzero" | "evenodd" } | { url: string }): EngravingLogo {
  const heightMM = prev?.heightMM ?? LOGO_HEIGHT.start;
  const mode: EngravingLogoMode = prev?.mode ?? "with-text";
  return "svg" in src ? { svg: src.svg, heightMM, mode, fillRule: src.fillRule } : { url: src.url, heightMM, mode };
}

/** Is the user signed in to a workspace (so a raster can become a Vault asset)? Same keys the API client reads. */
export function vaultSignedIn(): boolean {
  try {
    return !!localStorage.getItem("auth_token") && !!localStorage.getItem("active_workspace_id");
  } catch {
    return false;
  }
}

export function readFileAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(fr.error ?? new Error("could not read the file"));
    fr.readAsDataURL(file);
  });
}

/** A viewBox around a normalised logo path (for the preview), or null. */
export function logoViewBox(d: string): string | null {
  const segs = pathSegments(d);
  if (!segs?.length) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const s of segs) for (let i = 0; i < s.p.length; i += 2) {
    minX = Math.min(minX, s.p[i]); maxX = Math.max(maxX, s.p[i]);
    minY = Math.min(minY, s.p[i + 1]); maxY = Math.max(maxY, s.p[i + 1]);
  }
  if (!Number.isFinite(minX + maxX + minY + maxY)) return null;
  const pad = 0.06 * Math.max(maxX - minX, maxY - minY);
  return `${minX - pad} ${minY - pad} ${maxX - minX + 2 * pad} ${maxY - minY + 2 * pad}`;
}
